// src/components/onboarding/OnboardingGate.jsx
import React, { cloneElement, useCallback, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import { getStage } from "../../shared/_services/onboarding.services";
import { Button } from "../ui";
import Loader from "../Loader";
import { LOAD_FAILED, TRY_AGAIN } from "./onboardingCopy";
import { landingFor } from "./onboardingNav";

// Wraps each SP3 documents page (App.js). Reads GET /onboarding/stage on mount
// and on every change of `stageKey`, and compares the page's own stage key
// with the client's stage by position in the SERVED `stages` array (the app
// holds no order, R129):
//   - page after the client's stage → redirect to the client's page
//     (`routeFor.web`, or the legacy landing when that is null);
//   - page before it → render read-only (`readOnly: true`);
//   - the client's own page → render it.
// The child receives `readOnly`, `stage` (the whole stage payload) and
// `resumed` (arrived from a sign-in, not from the page before).
export default function OnboardingGate({ stageKey, children }) {
  const { user } = useAuth();
  const location = useLocation();
  // `forKey` is the page key a payload was read for. The router renders the
  // documents routes at the same tree position, so React keeps ONE gate
  // instance across /acknowledge → /agreement: the new page must never be
  // judged against the old page's payload. Without this the agreement page
  // saw the acknowledge payload, redirected back in that same render, and the
  // client stayed on Acknowledge after a successful submit (found by /qa
  // 2026-09-27, ISSUE-001).
  const [state, setState] = useState({
    status: "loading",
    data: null,
    forKey: stageKey,
  });

  const load = useCallback(async () => {
    setState({ status: "loading", data: null, forKey: stageKey });
    try {
      const res = await getStage();
      setState({ status: "ready", data: res.data, forKey: stageKey });
    } catch (_) {
      setState({ status: "error", data: null, forKey: stageKey });
    }
  }, [stageKey]);

  useEffect(() => {
    load();
  }, [load]);

  // A payload read for another page key is stale here: show the loader until
  // the read `load` just started for this key lands.
  // The intake pages are dark: the loader must not flash light before them (R200).
  if (state.status === "loading" || state.forKey !== stageKey)
    return stageKey === "intake" ? (
      <div className="stars-dark tw-min-h-screen tw-bg-bg tw-text-text">
        <Loader />
      </div>
    ) : (
      <Loader />
    );
  if (state.status === "error") {
    return (
      <div
        data-slot="onb-page"
        className="stars-dark tw-min-h-screen tw-bg-bg tw-font-sans tw-text-text"
      >
        <div
          data-slot="onb-load-failed"
          className="tw-px-8 tw-py-6 tw-text-sm max-rail:tw-px-4"
          role="alert"
        >
          <span>{LOAD_FAILED}</span>{" "}
          <Button variant="text" size="sm" onClick={load}>
            {TRY_AGAIN}
          </Button>
        </div>
      </div>
    );
  }

  const stage = state.data;
  const keys = (stage.stages || []).map((s) => s.key);
  const pageAt = keys.indexOf(stageKey);
  const clientAt = keys.indexOf(stage.stage);
  // R208: a stage the server does not list sends to the dashboard, never to a
  // route that could name this page again (landing → gate → landing loop).
  if (pageAt === -1 || clientAt === -1) {
    return <Navigate to="/dashboard" replace />;
  }
  if (pageAt > clientAt) {
    return <Navigate to={landingFor(stage, user)} replace />;
  }
  return cloneElement(children, {
    readOnly: pageAt < clientAt,
    stage,
    resumed: Boolean(location.state?.resumed),
  });
}
